/**
 * Where the printer put things, read back off the page image.
 *
 * The viewer draws every value as live text on the page, and for that to read
 * as the document rather than as a form laid over it, the text has to be the
 * size the PMS printed at, sit on the printed baseline, and hang from the spot
 * the printed value hung from. None of that is on the template. A template
 * stores the ruled cell, which is stable vector art, and the PMS puts its value
 * wherever its own report layout says inside that cell: on the reference
 * registration card "Khalil" starts a third of the way along its cell, "Thaer"
 * sits in the middle of its own, and the whole card is printed at 8pt, not at
 * the size the cells would suggest. So it is measured, per document, from the
 * pixels - which is also the only thing every capture has in common, since
 * some print-outs carry a Type3 text layer and some carry no text at all.
 *
 * Everything here is a pure function of an RGBA raster, so it is tested without
 * a browser. The viewer feeds it the page image through a canvas.
 */

/** A rectangle as fractions of the page, the same space the overlays use. */
export interface Box {
  x: number
  y: number
  w: number
  h: number
}

export interface Raster {
  data: ArrayLike<number>
  width: number
  height: number
}

/** The page font's proportions, as fractions of its size. */
export interface FontMetrics {
  capHeight: number
  ascent: number
  descent: number
}

export type Anchor = 'left' | 'center' | 'right'

export interface TextPlacement {
  /** The printed baseline, as a fraction of the page height. */
  baseline: number
  anchor: Anchor
  /** What the text hangs from - its left edge, centre or right edge - as a fraction of page width. */
  x: number
  /** The printed value's ink, to paper over before the live value is drawn. */
  erase: Box | null
}

export interface TickPlacement {
  /** Whether the print has a mark in the square. `null` when that could not be read. */
  printed: boolean | null
  /** The inside of the square, within its printed border. */
  interior: Box
}

export interface PageLayout {
  /** One size for the page, as a fraction of its width - a PMS prints every value alike. */
  fontSize: number
  text: Record<string, TextPlacement>
  ticks: Record<string, TickPlacement>
}

/** Arial's proportions, for when the browser cannot report its own. */
export const ARIAL: FontMetrics = { capHeight: 0.716, ascent: 0.905, descent: 0.212 }

/** 8pt on a 612pt-wide page: what the PMS prints at, and a sane size when nothing is printed. */
export const DEFAULT_FONT_SIZE = 8 / 612

/** Ink at or above this is part of a stroke. */
const SOLID = 0.5
/** A row or column this full of solid ink is a ruled line, not a glyph. */
const RULE = 0.85
/** A column whose run peaks this dark is a stem worth measuring. */
const STEM_PEAK = 0.8
/** The anti-aliased fringe a glyph leaves around its solid pixels. */
const FRINGE_PX = 2

interface Measured {
  left: number
  right: number
  baseline: number
  stemPx: number
  erase: { x0: number; y0: number; x1: number; y1: number } | null
}

export function layoutPage(
  raster: Raster,
  fields: { key: string; box: Box }[],
  ticks: { key: string; box: Box }[],
  metrics: FontMetrics = ARIAL,
): PageLayout {
  const { width: W, height: H } = raster
  const cells = fields.map(({ key, box }) => {
    const px = pixelBox(box, W, H)
    return { key, px, found: px ? measureValue(raster, px) : null }
  })

  const printed = cells.filter((c) => c.found && c.px) as {
    key: string
    px: PixelBox
    found: Measured
  }[]
  const sizes = printed.map((c) => c.found.stemPx / metrics.capHeight).filter((s) => s > 0)
  const fontPx = median(sizes) ?? DEFAULT_FONT_SIZE * W
  const tolerance = Math.max(2, fontPx * 0.5)

  const anchored = printed.map((c) => {
    const gapLeft = c.found.left - c.px.x0
    const gapRight = c.px.x1 - c.found.right
    const anchor: Anchor =
      gapLeft <= tolerance && gapRight > tolerance
        ? 'left'
        : gapRight <= tolerance && gapLeft > tolerance
          ? 'right'
          : 'center'
    return { ...c, anchor, gapLeft, gapRight, drop: c.px.y1 - c.found.baseline }
  })

  const commonAnchor = mode(anchored.map((c) => c.anchor)) ?? 'left'
  const commonDrop = median(anchored.map((c) => c.drop)) ?? (metrics.descent + 0.1) * fontPx
  const leftGap = median(anchored.filter((c) => c.anchor === 'left').map((c) => c.gapLeft))
  const rightGap = median(anchored.filter((c) => c.anchor === 'right').map((c) => c.gapRight))

  const text: Record<string, TextPlacement> = {}
  for (const { key, px, found } of cells) {
    if (!px) continue
    const own = anchored.find((c) => c.key === key)
    if (own && found) {
      text[key] = {
        baseline: found.baseline / H,
        anchor: own.anchor,
        x:
          (own.anchor === 'left'
            ? found.left
            : own.anchor === 'right'
              ? found.right
              : (found.left + found.right) / 2) / W,
        erase: found.erase
          ? {
              x: found.erase.x0 / W,
              y: found.erase.y0 / H,
              w: (found.erase.x1 - found.erase.x0) / W,
              h: (found.erase.y1 - found.erase.y0) / H,
            }
          : null,
      }
      continue
    }

    // Nothing printed here. Its neighbours on the same row know where a value
    // on this row sits; failing that, the page as a whole does.
    const row = anchored.filter(
      (c) => Math.abs(c.px.y0 - px.y0) <= 2 && Math.abs(c.px.y1 - px.y1) <= 2,
    )
    const anchor = mode(row.map((c) => c.anchor)) ?? commonAnchor
    const drop = median(row.map((c) => c.drop)) ?? commonDrop
    const x =
      anchor === 'left'
        ? px.x0 + (leftGap ?? fontPx * 0.3)
        : anchor === 'right'
          ? px.x1 - (rightGap ?? fontPx * 0.3)
          : (px.x0 + px.x1) / 2
    text[key] = { baseline: (px.y1 - drop) / H, anchor, x: x / W, erase: null }
  }

  const tickPlacements: Record<string, TickPlacement> = {}
  for (const { key, box } of ticks) {
    const px = pixelBox(box, W, H)
    tickPlacements[key] = px ? measureTick(raster, px) : { printed: null, interior: inset(box) }
  }

  return { fontSize: fontPx / W, text, ticks: tickPlacements }
}

/**
 * When the page cannot be read - no canvas, or an image the browser will not
 * let a script look at. Every value is still drawn, so every cell is papered
 * over whole: a printed value left showing under a different live one is two
 * answers in one box.
 */
export function fallbackLayout(
  fields: { key: string; box: Box }[],
  ticks: { key: string; box: Box }[],
  aspect: number,
  metrics: FontMetrics = ARIAL,
): PageLayout {
  const fontSize = DEFAULT_FONT_SIZE
  const drop = ((metrics.descent + 0.1) * fontSize) / aspect
  const text: Record<string, TextPlacement> = {}
  for (const { key, box } of fields) {
    text[key] = {
      baseline: box.y + box.h - drop,
      anchor: 'left',
      x: box.x + fontSize * 0.3,
      erase: inset(box, 0.002, 0.0015),
    }
  }
  const tickPlacements: Record<string, TickPlacement> = {}
  for (const { key, box } of ticks) tickPlacements[key] = { printed: null, interior: inset(box) }
  return { fontSize, text, ticks: tickPlacements }
}

/** One run of erased rows and the paper that fills them, in page pixels. */
export interface RowFill {
  x: number
  y: number
  w: number
  h: number
  rgba: [number, number, number, number]
}

/** The patches to lift for a layout: each printed value's ink, within its own cell. */
export function erasePatches(
  layout: PageLayout,
  fields: { key: string; box: Box }[],
): { erase: Box; within: Box }[] {
  return fields.flatMap(({ key, box }) => {
    const erase = layout.text[key]?.erase
    return erase ? [{ erase, within: box }] : []
  })
}

/**
 * The page with its printed values lifted off, as a careful hand with
 * correction fluid would do it: each erased row is refilled with that row's
 * own paper, taken from the rest of its cell either side of the value.
 *
 * Not a white rectangle. White is right on white paper and wrong anywhere
 * else - on the anti-aliased edge of a rule that a descender ran into it cuts
 * a notch in the line, and on a shaded cell it is a white box.
 *
 * Returned as drawing instructions rather than as a new page: the values are
 * about 1% of the page's pixels, and copying all of it (twice) and putting
 * the copy back was most of what made a pushed document slow to appear on a
 * tablet. Rows that follow on with the same paper are one run.
 */
export function inpaintFills(
  raster: Raster,
  patches: { erase: Box; within: Box }[],
): RowFill[] {
  const { width: W, height: H, data } = raster
  const luma = (i: number) => 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  const fills: RowFill[] = []

  for (const { erase, within } of patches) {
    const e = pixelEdges(erase, W, H)
    const cell = pixelEdges(within, W, H)
    if (e.x1 <= e.x0) continue
    let run: RowFill | null = null
    for (let y = e.y0; y < e.y1; y++) {
      const row: number[] = []
      for (let x = cell.x0; x < cell.x1; x++) {
        if (x < e.x0 || x >= e.x1) row.push((y * W + x) * 4)
      }
      row.sort((a, b) => luma(a) - luma(b))
      const m = row[row.length >> 1]
      const rgba: RowFill['rgba'] =
        m === undefined ? [255, 255, 255, 255] : [data[m], data[m + 1], data[m + 2], data[m + 3]]
      if (run && run.rgba.every((c, i) => c === rgba[i])) {
        run.h += 1
      } else {
        run = { x: e.x0, y, w: e.x1 - e.x0, h: 1, rgba }
        fills.push(run)
      }
    }
  }
  return fills
}

/** `inpaintFills` applied to a copy of the page, for callers that want pixels. */
export function inpaint(
  raster: Raster,
  patches: { erase: Box; within: Box }[],
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(raster.data)
  for (const { x, y, w, h, rgba } of inpaintFills(raster, patches)) {
    for (let row = y; row < y + h; row++) {
      for (let col = x; col < x + w; col++) out.set(rgba, (row * raster.width + col) * 4)
    }
  }
  return out
}

/**
 * Where text of this width starts, so it hangs from its anchor like the print
 * did - but never out of its cell. A centred value that has grown past the
 * room on one side is pushed back in rather than run over a rule.
 */
export function textStart(place: TextPlacement, box: Box, width: number): number {
  const start =
    place.anchor === 'left'
      ? place.x
      : place.anchor === 'right'
        ? place.x - width
        : place.x - width / 2
  return Math.max(box.x, Math.min(start, box.x + box.w - width))
}

// ----------------------------------------------------------------- pixels

interface PixelBox {
  x0: number
  y0: number
  x1: number
  y1: number
}

function pixelEdges(box: Box, W: number, H: number): PixelBox {
  return {
    x0: clamp(Math.round(box.x * W), 0, W),
    x1: clamp(Math.round((box.x + box.w) * W), 0, W),
    y0: clamp(Math.round(box.y * H), 0, H),
    y1: clamp(Math.round((box.y + box.h) * H), 0, H),
  }
}

/** The box in pixels, or null when it is too small to measure anything in. */
function pixelBox(box: Box, W: number, H: number): PixelBox | null {
  const px = pixelEdges(box, W, H)
  return px.x1 - px.x0 >= 2 && px.y1 - px.y0 >= 2 ? px : null
}

/** How dark a pixel is, 0 for paper to 1 for solid ink. Transparent is paper. */
function inkAt(r: Raster, x: number, y: number): number {
  const i = (y * r.width + x) * 4
  const d = r.data
  const luminance = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]
  return (1 - luminance / 255) * (d[i + 3] / 255)
}

function rowFullness(r: Raster, y: number, x0: number, x1: number): number {
  let solid = 0
  for (let x = x0; x < x1; x++) if (inkAt(r, x, y) >= SOLID) solid++
  return solid / Math.max(1, x1 - x0)
}

function colFullness(r: Raster, x: number, y0: number, y1: number): number {
  let solid = 0
  for (let y = y0; y < y1; y++) if (inkAt(r, x, y) >= SOLID) solid++
  return solid / Math.max(1, y1 - y0)
}

/**
 * The printed value in one cell: its horizontal extent, its baseline, the
 * height of its tallest stem, and the ink to paper over.
 *
 * Ruled lines are not a value. A box drawn to the cell's own edge - a field
 * with no caption, placed by hand - holds its border, so any row or column
 * that is nearly all ink is set aside, with the pixel either side of it, which
 * is where its anti-aliasing lives.
 */
function measureValue(r: Raster, box: PixelBox): Measured | null {
  const { x0, x1, y0 } = box
  let y1 = box.y1

  // A descender can hang a pixel or two out of the value band. Reach down for
  // it, but measure nothing from a ruled line or the fringe just above one.
  const reach = Math.max(2, Math.round(r.height * 0.004))
  for (let y = box.y1; y < Math.min(r.height, box.y1 + reach); y++) {
    const next = y + 1 < r.height ? rowFullness(r, y + 1, x0, x1) : 0
    if (rowFullness(r, y, x0, x1) > RULE || next > RULE) break
    y1 = y + 1
  }
  // The erase may go further than the measurement, down to the rule itself:
  // on the registration card the "p" and "y" of "Company / Travel Agent" run
  // into the rule's anti-aliased edge, and stopping short of that row left
  // their tips standing on the line. `inpaint` repaints that row as the rule
  // edge it is, so taking it costs nothing.
  let floor = y1
  while (floor < Math.min(r.height, y1 + 2) && rowFullness(r, floor, x0, x1) <= RULE) floor++
  if (floor >= r.height || rowFullness(r, floor, x0, x1) <= RULE) floor = y1

  const ruleRows = new Set<number>()
  const maskedRows = new Set<number>()
  for (let y = y0; y < y1; y++) {
    if (rowFullness(r, y, x0, x1) > RULE) {
      ruleRows.add(y)
      ;[y - 1, y, y + 1].forEach((v) => maskedRows.add(v))
    }
  }
  const maskedCols = new Set<number>()
  for (let x = x0; x < x1; x++) {
    if (colFullness(r, x, y0, y1) > RULE) [x - 1, x, x + 1].forEach((v) => maskedCols.add(v))
  }

  // Bottoms are taken per glyph - a run of inked columns - not per column. A
  // column's lowest pixel is not always a glyph's: across a "4" most columns
  // end at the crossbar, and in "004" they outvoted the baseline.
  const bottoms: number[] = []
  let glyphBottom = -1
  let left = -1
  let right = -1
  let top = Infinity
  let lowest = -1
  let stem = 0
  let solidPixels = 0
  let inkedColumns = 0

  const endGlyph = () => {
    if (glyphBottom >= 0) bottoms.push(glyphBottom)
    glyphBottom = -1
  }

  for (let x = x0; x < x1; x++) {
    if (maskedCols.has(x)) {
      endGlyph()
      continue
    }
    let first = -1
    let last = -1
    let run = 0
    let peak = 0
    const close = () => {
      if (peak >= STEM_PEAK) stem = Math.max(stem, run)
      run = 0
      peak = 0
    }
    for (let y = y0; y < y1; y++) {
      if (maskedRows.has(y)) {
        close()
        continue
      }
      const v = inkAt(r, x, y)
      if (v >= SOLID) {
        if (first < 0) first = y
        last = y
        solidPixels++
      }
      if (v > 0.02) {
        run += v
        peak = Math.max(peak, v)
      } else {
        close()
      }
    }
    close()
    if (first < 0) {
      endGlyph()
      continue
    }
    if (left < 0) left = x
    right = x + 1
    top = Math.min(top, first)
    lowest = Math.max(lowest, last)
    glyphBottom = Math.max(glyphBottom, last + 1)
    inkedColumns++
  }
  endGlyph()

  // A speck is not a value. Two columns and a handful of pixels is the
  // smallest thing worth calling one - the stem of a "1" at 150dpi.
  if (inkedColumns < 2 || solidPixels < 4) return null

  // The baseline is where the middle glyph stops. Descenders are the minority
  // on any line of print, so they do not move it.
  const baseline = median(bottoms) ?? lowest + 1

  const clampX = (v: number) => clamp(v, x0, x1)
  const erase = {
    x0: clampX(left - FRINGE_PX),
    y0: clamp(top - FRINGE_PX, y0, floor),
    x1: clampX(right + FRINGE_PX),
    y1: clamp(lowest + 1 + FRINGE_PX, y0, floor),
  }
  // Padding must never take a rule. A horizontal rule's fringe row may go -
  // `inpaint` restores it from the rest of its row - but a vertical rule's
  // fringe may not, since that is refilled from the row, which is paper.
  while (erase.y1 > erase.y0 && ruleRows.has(erase.y1 - 1)) erase.y1--
  while (erase.y0 < erase.y1 && ruleRows.has(erase.y0)) erase.y0++
  while (erase.x1 > erase.x0 && maskedCols.has(erase.x1 - 1)) erase.x1--
  while (erase.x0 < erase.x1 && maskedCols.has(erase.x0)) erase.x0++

  return {
    left,
    right,
    baseline,
    stemPx: stem,
    erase: erase.x1 > erase.x0 && erase.y1 > erase.y0 ? erase : null,
  }
}

/**
 * A tick box: where the inside of its printed border is, and whether the print
 * put a mark there.
 *
 * The border is found rather than assumed, scanning in from just outside the
 * box until the columns and rows stop being nearly all ink. That matters
 * because papering over a printed tick must stop exactly at the border: a
 * pixel short and a sliver of tick is left, a pixel over and the square loses
 * a side. The scan starts one pixel out and no further, because on the
 * registration card's payment band the next ruled line is 1.4pt below the
 * squares, and a scan that started beyond it would take the rule for the
 * square's own edge.
 */
function measureTick(r: Raster, box: PixelBox): TickPlacement {
  const W = r.width
  const H = r.height
  const w = box.x1 - box.x0
  const h = box.y1 - box.y0
  const pad = 1
  const edge = 0.25
  // A printed border line is ink the whole way along; the heavy tick on the
  // registration card fills 60% of some columns, so the bar sits well above it.
  const full = 0.8

  const colFull = (x: number) => {
    let n = 0
    for (let y = box.y0; y < box.y1; y++) if (inkAt(r, x, y) > edge) n++
    return n / h
  }
  const rowFull = (y: number) => {
    let n = 0
    for (let x = box.x0; x < box.x1; x++) if (inkAt(r, x, y) > edge) n++
    return n / w
  }

  /** Scan from `from` towards `to`; return the first position past the border, or null. */
  const inside = (from: number, to: number, fullness: (v: number) => number) => {
    const step = from < to ? 1 : -1
    let v = from
    while (v !== to && fullness(v) <= full) v += step
    if (v === to) return null
    while (v !== to && fullness(v) > full) v += step
    return v === to ? null : v
  }

  const midX = Math.round((box.x0 + box.x1) / 2)
  const midY = Math.round((box.y0 + box.y1) / 2)
  const left = inside(clamp(box.x0 - pad, 0, W - 1), midX, colFull)
  const right = inside(clamp(box.x1 + pad - 1, 0, W - 1), midX, colFull)
  const top = inside(clamp(box.y0 - pad, 0, H - 1), midY, rowFull)
  const bottom = inside(clamp(box.y1 + pad - 1, 0, H - 1), midY, rowFull)

  const found =
    left !== null && right !== null && top !== null && bottom !== null &&
    right - left >= 2 && bottom - top >= 2
  const interior = found
    ? { x0: left, y0: top, x1: right + 1, y1: bottom + 1 }
    : {
        x0: box.x0 + Math.round(w * 0.18),
        y0: box.y0 + Math.round(h * 0.18),
        x1: box.x1 - Math.round(w * 0.18),
        y1: box.y1 - Math.round(h * 0.18),
      }

  let marked = 0
  for (let y = interior.y0; y < interior.y1; y++) {
    for (let x = interior.x0; x < interior.x1; x++) if (inkAt(r, x, y) >= SOLID) marked++
  }
  const area = Math.max(1, (interior.x1 - interior.x0) * (interior.y1 - interior.y0))

  return {
    printed: marked >= Math.max(2, area * 0.04),
    interior: {
      x: interior.x0 / W,
      y: interior.y0 / H,
      w: (interior.x1 - interior.x0) / W,
      h: (interior.y1 - interior.y0) / H,
    },
  }
}

// ------------------------------------------------------------------- bits

function inset(box: Box, dx = box.w * 0.18, dy = box.h * 0.18): Box {
  return {
    x: box.x + dx,
    y: box.y + dy,
    w: Math.max(0, box.w - 2 * dx),
    h: Math.max(0, box.h - 2 * dy),
  }
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value))
}

function median(values: number[]): number | null {
  if (!values.length) return null
  const sorted = [...values].sort((a, b) => a - b)
  const middle = sorted.length >> 1
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

/** The most common value; on a tie, the one seen first. */
function mode<T>(values: T[]): T | null {
  if (!values.length) return null
  const counts = new Map<T, number>()
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1)
  let best: T | null = null
  let most = 0
  for (const [value, count] of counts) {
    if (count > most) {
      best = value
      most = count
    }
  }
  return best
}
